import { useState, useCallback, useMemo, useEffect } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { Download, RefreshCw } from "lucide-react";
import { trpc } from "@/lib/trpc";
import { Page } from "@/components/layout/page";
import { Button } from "@/components/ui/button";
import {
  ActivityFilterBar,
  ActivityFeedList,
  type ActivityKindFilter,
  type ActivityActorFilter,
  type ProjectActivityEntry,
} from "@/components/activity";

export function ProjectActivityPage() {
  const { id: projectId } = useParams<{ id: string }>();
  const [searchParams, setSearchParams] = useSearchParams();

  const kindParam = (searchParams.get("kind") as ActivityKindFilter) || "all";
  const actorParam =
    (searchParams.get("actor") as ActivityActorFilter) || "all";
  const searchParam = searchParams.get("q") || "";

  const [searchInput, setSearchInput] = useState(searchParam);

  const updateParam = useCallback(
    (key: string, value: string | null) => {
      const next = new URLSearchParams(searchParams);
      if (value && value !== "all" && value !== "") {
        next.set(key, value);
      } else {
        next.delete(key);
      }
      setSearchParams(next, { replace: true });
    },
    [searchParams, setSearchParams],
  );

  // Sync external searchParam changes (e.g. back/forward navigation)
  useEffect(() => {
    setSearchInput(searchParam);
  }, [searchParam]);

  // Debounce search query commitment to URL params and tRPC query
  useEffect(() => {
    const timer = setTimeout(() => {
      const trimmed = searchInput.trim();
      if (trimmed !== searchParam) {
        updateParam("q", trimmed || null);
      }
    }, 250);
    return () => clearTimeout(timer);
  }, [searchInput, searchParam, updateParam]);

  const handleKindChange = useCallback(
    (kind: ActivityKindFilter) => {
      updateParam("kind", kind === "all" ? null : kind);
    },
    [updateParam],
  );

  const handleActorChange = useCallback(
    (actor: ActivityActorFilter) => {
      updateParam("actor", actor === "all" ? null : actor);
    },
    [updateParam],
  );

  const handleSearchChange = useCallback((val: string) => {
    setSearchInput(val);
  }, []);

  const handleReset = useCallback(() => {
    setSearchInput("");
    const next = new URLSearchParams();
    setSearchParams(next, { replace: true });
  }, [setSearchParams]);

  // Infinite query for cursor-based activity pagination
  const {
    data,
    isLoading,
    isFetching,
    isFetchingNextPage,
    hasNextPage,
    fetchNextPage,
    refetch,
    error,
  } = trpc.workflow.projectActivity.useInfiniteQuery(
    {
      projectId: projectId!,
      kind: kindParam,
      actorKind: actorParam,
      search: searchParam || undefined,
      limit: 25,
    },
    {
      enabled: Boolean(projectId),
      getNextPageParam: (lastPage) => lastPage.nextCursor ?? undefined,
      initialCursor: null,
      refetchInterval: 15_000,
    },
  );

  const allEntries = useMemo<ProjectActivityEntry[]>(() => {
    return data?.pages.flatMap((page) => page.entries) ?? [];
  }, [data]);

  const handleExportJson = useCallback(() => {
    if (!allEntries.length) return;
    const blob = new Blob([JSON.stringify(allEntries, null, 2)], {
      type: "application/json",
    });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `project-${projectId}-activity-audit.json`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  }, [allEntries, projectId]);

  if (!projectId) return null;

  return (
    <Page
      title="Activity"
      description="Chronological audit trail of state changes, triage notes, walkthrough runs, and issue tracker sync."
      maxWidth="max-w-4xl"
      actions={
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => refetch()}
            disabled={isFetching}
            className="text-xs"
            title="Refresh feed"
          >
            <RefreshCw
              className={`size-3.5 mr-1.5 ${isFetching ? "animate-spin" : ""}`}
            />
            Refresh
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={handleExportJson}
            disabled={allEntries.length === 0}
            className="text-xs"
            title="Download loaded audit records as JSON"
          >
            <Download className="size-3.5 mr-1.5" />
            Export JSON
          </Button>
        </div>
      }
    >
      <div className="rounded-lg border border-border/80 bg-card p-4 sm:p-6 space-y-4 shadow-xs">
        <ActivityFilterBar
          kind={kindParam}
          onKindChange={handleKindChange}
          actor={actorParam}
          onActorChange={handleActorChange}
          search={searchInput}
          onSearchChange={handleSearchChange}
          onReset={handleReset}
          totalLoaded={allEntries.length}
          isLoading={isLoading}
        />

        <ActivityFeedList
          entries={allEntries}
          projectId={projectId}
          isLoading={isLoading}
          isFiltering={isFetching && !isLoading && !isFetchingNextPage}
          isFetchingNextPage={isFetchingNextPage}
          hasNextPage={Boolean(hasNextPage)}
          fetchNextPage={() => fetchNextPage()}
          hasActiveFilters={
            kindParam !== "all" || actorParam !== "all" || Boolean(searchParam)
          }
          onResetFilters={handleReset}
          error={error}
          onRetry={() => refetch()}
        />
      </div>
    </Page>
  );
}
